// ============================================================
// Summit League v2 - Toast Hook
// ============================================================

import { useState, useCallback, useRef } from 'react';
import type { Toast } from '../components/ui/Toast';

let toastIdCounter = 0;

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (type: Toast['type'], title: string, message?: string, duration = 5000) => {
      const id = `toast-${++toastIdCounter}`;
      const toast: Toast = { id, type, title, message, duration };

      setToasts((prev) => [...prev, toast]);

      return id;
    },
    []
  );

  const success = useCallback(
    (title: string, message?: string) => addToast('success', title, message),
    [addToast]
  );

  const error = useCallback(
    (title: string, message?: string) => addToast('error', title, message, 7000),
    [addToast]
  );

  const warning = useCallback(
    (title: string, message?: string) => addToast('warning', title, message),
    [addToast]
  );

  const info = useCallback(
    (title: string, message?: string) => addToast('info', title, message),
    [addToast]
  );

  return { toasts, addToast, removeToast, success, error, warning, info };
}
